/**
 * The canvas, below 640px, as a list.
 *
 * A pannable canvas on a phone is a map you cannot read: at a scale where a
 * WhatsApp card's text is legible you see one step, and at a scale where you
 * see the shape you read nothing. So below `sm` the editor draws the same
 * graph as a list in reading order (breadth-first from Start, then anything
 * not connected, then comments), each step a button that opens its settings
 * beneath it, each of its paths a line — "Not now → Exit" — and each free path
 * a real "+ Add a step after “Not now”" button that opens the same add menu
 * inline. Nothing is phone-only: it is the same graph, the same menu, the same
 * drawer, the same autosave.
 */
import { edgeFrom, portsOf, type FlowGraph, type FlowNode, type FlowNodeStats } from '@wend/shared';
import { cn } from '@wend/ui';
import type { ReactNode } from 'react';

import { NodeIcon, nodeTone, type FlowMode } from './Node';
import { conditionLabel, goalLabel, n, nodeName, readingOrder, updateLabel, waitText, type FlowEditorData } from './model';

function summary(node: FlowNode, data: FlowEditorData, triggerSummary: string): string {
  switch (node.type) {
    case 'trigger':
      return triggerSummary;
    case 'message': {
      const tpl = node.data.templateId ? data.templates.find((t) => t.id === node.data.templateId) : undefined;
      return tpl ? tpl.name : 'No template chosen';
    }
    case 'wait':
      return waitText(node.data);
    case 'condition':
      return conditionLabel(node.data, data.fields);
    case 'split':
      return [...node.data.rows.map((r) => `${r.label} ${r.pct}%`), ...(node.data.holdout ? [`Holdout ${node.data.holdout.pct}%`] : [])].join(' · ');
    case 'update':
      return updateLabel(node.data);
    case 'goal':
      return goalLabel(node.data.outcomeTypeId, data.outcomeTypes);
    case 'exit':
      return node.data.note ?? 'Leaves the flow';
    case 'comment':
      return node.data.text;
  }
}

export interface FlowListProps {
  graph: FlowGraph;
  data: FlowEditorData;
  mode: FlowMode;
  triggerSummary: string;
  selected: string | null;
  menuKey: string | null;
  stats: ReadonlyMap<string, FlowNodeStats> | null;
  onSelect: (id: string) => void;
  onAdd: (nodeId: string, portId: string) => void;
  /** The drawer, rendered under the selected step. */
  drawer: ReactNode;
  /** The add menu, rendered under the port it belongs to. */
  menu: ReactNode;
}

export function FlowList({ graph, data, mode, triggerSummary, selected, menuKey, stats, onSelect, onAdd, drawer, menu }: FlowListProps) {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  return (
    <ol className="m-0 flex list-none flex-col gap-2.5 p-3" aria-label="Steps, in order" data-role="flow-list">
      {readingOrder(graph).map((node) => {
        const shape = node.type === 'message' && node.data.channel === 'whatsapp' && node.data.templateId ? data.waShapes.get(node.data.templateId) : undefined;
        const stat = stats?.get(node.id);
        return (
          // The same focus hooks as the canvas (`data-node`, `data-head`,
          // `data-addport`): below 640px the canvas is hidden and this list is
          // what the editor returns focus to after a menu or drawer closes.
          <li key={node.id} data-node={node.id} className={cn('rounded-xl border bg-surface-2', node.suggested ? 'border-dashed border-[#B08CFF]' : 'border-line-strong')}>
            <button
              type="button"
              data-head=""
              aria-pressed={selected === node.id}
              onClick={() => onSelect(node.id)}
              className="flex w-full cursor-pointer items-start gap-2.5 rounded-xl border-0 bg-transparent px-3 py-2.5 text-left focus-visible:outline-2 focus-visible:outline-brand-b"
            >
              <span className={cn('mt-0.5 grid size-7 flex-none place-items-center rounded-lg bg-white/[0.07]', nodeTone(node))}>
                <NodeIcon node={node} size={15} />
              </span>
              <span className="min-w-0">
                <span className="block text-[13.5px] font-semibold text-white">
                  {nodeName(node)}
                  {node.suggested && <span className="ml-2 text-[11px] font-medium text-brand-ink">Suggested</span>}
                </span>
                <span className="block text-[12.5px] text-muted">{summary(node, data, triggerSummary)}</span>
                {stat && <span className="block text-[11.5px] text-muted">{n(stat.entered)} entered · {n(stat.waitingNow)} in step · {n(stat.converted)} converted</span>}
              </span>
            </button>
            {portsOf(node, shape ?? null).length > 0 && (
              <ul className="m-0 flex list-none flex-col gap-1 border-t border-line px-3 py-2">
                {portsOf(node, shape ?? null).map((port) => {
                  const edge = edgeFrom(graph, node.id, port.id);
                  const to = edge ? byId.get(edge.to) : undefined;
                  const key = `${node.id}:${port.id}`;
                  return (
                    <li key={port.id} className="text-[12.5px] text-muted">
                      {to ? (
                        <span>
                          {port.label} → <b className="font-medium text-white">{nodeName(to)}</b>
                          {stats && edge ? ` · ${n(stats.get(node.id)?.ports[port.id] ?? 0)}` : ''}
                        </span>
                      ) : mode === 'edit' && port.kind !== 'holdout' && !node.suggested ? (
                        <button
                          type="button"
                          data-addport={key}
                          aria-expanded={menuKey === key}
                          aria-haspopup="menu"
                          onClick={() => onAdd(node.id, port.id)}
                          className="cursor-pointer rounded-md border border-dashed border-line-strong px-2 py-1 text-[12.5px] text-text focus-visible:outline-2 focus-visible:outline-brand-b"
                        >
                          + Add a step after “{port.label}”
                        </button>
                      ) : (
                        <span>{port.label} → {port.kind === 'holdout' ? 'held out, gets nothing' : 'leaves the flow'}</span>
                      )}
                      {menuKey === key && menu}
                    </li>
                  );
                })}
              </ul>
            )}
            {selected === node.id && <div className="px-2 pb-2">{drawer}</div>}
          </li>
        );
      })}
    </ol>
  );
}
